/*
* Project: BookingEngine
* Module: components/common/ShareWithOthers.js
* vim:ft=javascript.jsx:
*/
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  resetPassword, setShowRecoverPassword, setShowRegistration,
  checkRegistrationEmail, resendActivationLink,
} from '../../actions/Loyalty';
import CloseIcon from '../icons/CloseIcon';
import './recoverPassword.less'; // <-- Importamos el CSS
import LoyaltyEmailStatusHelp, { getEmailInputStatusClass } from './LoyaltyEmailStatusHelp';
import LoyaltyActivationLinkSentModal from './LoyaltyActivationLinkSentModal';
import {
  EMAIL_FORM, EmailStatusChecker, canResendActivationLink, getInitialEmailCheckState,
  isEmailFormatValid, isEmailStatusSettled, isSubmitBlockedByEmailStatus, RESEND_ERROR_THROTTLED,
  takeForgotPasswordPrefillEmail,
} from '../../utils/loyaltyEmailStatus';

class RecoverPasswordModal extends React.Component {
  constructor(props) {
    super(props);
    this.state = this.getResetState();

    this.emailChecker = new EmailStatusChecker({
      check: (email) => this.props.dispatch(checkRegistrationEmail(email)),
      onUpdate: (update) => this.setState(update),
    });
  }

  componentDidMount() {
    // Viene del registro (email ACTIVE -> "Click to receive password reset instructions")
    const prefillEmail = takeForgotPasswordPrefillEmail();
    if (prefillEmail) {
      this.setState({ email: { ...this.state.email, value: prefillEmail.toLowerCase() } });
    }
  }

  componentWillUnmount() {
    this.emailChecker.dispose();
  }

  getResetState() {
    return {
      showRecoverSuccess: false,
      recoverError: '',
      loading: false,
      email: {
        name: 'email',
        value: '',
        valid: undefined,
        msg: '',
        required: true,
      },
      // Pre-check del email en loyalty (checkRegistrationEmail)
      ...getInitialEmailCheckState(),
    };
  }

  renderLoading() {
    if (!this.state.loading) {
      return null;
    }
    return (
      <div className="recoverPassword-loading">
        <img src={'/img/loading5.gif'} alt={'loading3'}/>
        <p>{this.context.t('Wait a moment, please...')}</p>
      </div>
    );
  }

  // Regresa el status del email actual; si aún no lo tenemos, lo consulta en ese momento
  ensureEmailStatus = async () => {
    const email = this.state.email.value;
    if (!isEmailFormatValid(email)) {
      return null;
    }
    if (isEmailStatusSettled(this.state, email)) {
      return this.state.emailStatus;
    }
    const status = await this.emailChecker.checkNow(email);
    return status === undefined ? null : status;
  };

  /*
  * Si el reset falla, reconsultamos el check: si el email resultó estar
  * NOT_FOUND / PENDING / UNSUBSCRIBED / BLOCKED, el mensaje correcto (con su CTA)
  * ya queda debajo del input; si no, mostramos el error genérico.
  */
  applyRecoverFailure = async () => {
    const status = await this.emailChecker.checkNow(this.state.email.value);
    this.setState({
      loading: false,
      recoverError: isSubmitBlockedByEmailStatus(EMAIL_FORM.FORGOT, status)
        ? ''
        : this.context.t('An error has occurred'),
    });
  };

  onRecoverPassword = async (event) => {
    event.preventDefault();
    if (this.state.loading) {
      return;
    }
    this.setState({ loading: true, recoverError: '' });

    const emailStatus = await this.ensureEmailStatus();
    if (isSubmitBlockedByEmailStatus(EMAIL_FORM.FORGOT, emailStatus)) {
      // El reset sólo aplica a cuentas ACTIVE; el motivo ya se muestra bajo el input
      this.setState({ loading: false });
      return;
    }

    this.props.dispatch(resetPassword(this.state.email.value.trim()))
      .then((response) => {
        if (response && response.success) {
          this.setState({ showRecoverSuccess: true, loading: false });
        } else {
          this.applyRecoverFailure();
        }
      })
      .catch(() => {
        this.setState({ loading: false, recoverError: this.context.t('An error has occurred') });
      });
  }

  onChangeValue = (fieldName, event) => {
    let { value } = event.target;
    // El email siempre en minúsculas (al teclear o pegar)
    if (fieldName === 'email' && typeof value === 'string') {
      value = value.toLowerCase();
    }
    const updatedState = {
      value,
    };
    this.setState({ [fieldName]: { ...this.state[fieldName], ...updatedState } });
    if (fieldName === 'email') {
      this.setState({ recoverError: '', resendError: '' });
      this.emailChecker.schedule(value);
    }
  }

  // NOT_FOUND -> "Click to register"
  onEmailStatusRegister = () => {
    this.props.dispatch(setShowRegistration(true));
    this.props.dispatch(setShowRecoverPassword(false));
  };

  // PENDING_VERIFICATION / UNSUBSCRIBED -> reenviar link de activación
  onResendActivationLink = () => {
    const email = this.state.email.value.trim();
    if (this.state.isResending || !canResendActivationLink(this.state.emailStatus) || !email) {
      return;
    }
    this.setState({ isResending: true, resendError: '' });
    this.props.dispatch(resendActivationLink(email))
      .then(() => {
        this.setState({ isResending: false, showLinkSentModal: true });
      })
      .catch((error) => {
        this.setState({
          isResending: false,
          resendError: error && error.code === RESEND_ERROR_THROTTLED
            ? this.context.t('Please wait a moment before requesting another link.')
            : this.context.t('We could not send the link. Please try again.'),
        });
      });
  };

  // Tras reenviar el link ya no tiene sentido el reset: cerramos el popup
  onCloseLinkSentModal = () => {
    this.setState({ showLinkSentModal: false });
    this.props.dispatch(setShowRecoverPassword(false));
  };

  renderRecoverPasswordForm() {
    return (
      <div className="recoverPassword-inner">
        {this.renderLoading()}
        <CloseIcon
          onClick={() => this.props.dispatch(setShowRecoverPassword(false))}
          className="recoverPassword-close"
        />
        <h1 className="recoverPassword-title">
          {this.context.t('Forgotten password?')}
        </h1>
        <p className="recoverPassword-description text-gray-900">
          {this.context.t(
            'If you have forgotten your password, we will send you a recover email',
          )}
        </p>
        <form className="mt-3" onSubmit={this.onRecoverPassword}>
          <div className="FormControl email mb-3">
            <label htmlFor="inp_email" className="recoverPassword-label">
              Email<span>*</span>
            </label>
            <input
              type="email"
              required
              id="inp_email"
              className={[
                'recoverPassword-email form-input form-control',
                getEmailInputStatusClass(
                  EMAIL_FORM.FORGOT,
                  this.state.emailStatus,
                  this.state.emailCheckLoading,
                  this.state.emailCheckFailed,
                ),
              ].filter(Boolean).join(' ')}
              value={this.state.email.value}
              onChange={(e) => this.onChangeValue('email', e)}
              aria-describedby="loyalty_forgot_email_status"
              autoComplete="email"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
            />
            <LoyaltyEmailStatusHelp
              id="loyalty_forgot_email_status"
              t={this.context.t}
              form={EMAIL_FORM.FORGOT}
              status={this.state.emailStatus}
              loading={this.state.emailCheckLoading}
              checkFailed={this.state.emailCheckFailed}
              isResending={this.state.isResending}
              resendError={this.state.resendError}
              onRegister={this.onEmailStatusRegister}
              onResend={this.onResendActivationLink}
            />
          </div>

          {this.state.recoverError && (
            <div className="recoverPassword-error">
              {this.state.recoverError}
            </div>
          )}

          <div className="recoverPassword-actions">
            <button
              className="recoverPassword-button-send blue-style"
              type="submit"
              disabled={this.state.loading
                || isSubmitBlockedByEmailStatus(EMAIL_FORM.FORGOT, this.state.emailStatus)}
            >
              {this.state.loading ? this.context.t('Sending...') : this.context.t('Send')}
            </button>

            <button
              onClick={() => this.props.dispatch(setShowRecoverPassword(false))}
              className="recoverPassword-button-cancel white-style"
              type="button"
              disabled={this.state.loading}
            >
              {this.context.t('Cancel')}
            </button>
          </div>
        </form>
      </div>
    );
  }

  renderSuccessModal() {
    return (
      <div className="recoverPassword-inner">
        {this.renderLoading()}
        <CloseIcon
          onClick={() => this.props.dispatch(setShowRecoverPassword(false))}
          className="recoverPassword-close"
        />
        <h1 className="recoverPassword-title">
          {this.context.t('Forgotten your password?')}
        </h1>
        <p className="recoverPassword-description ml-1">
          {this.context.t('An email has been sent to you to recover your password.')}
        </p>
        <div className="recoverPassword-success">
          <button
            onClick={() => this.props.dispatch(setShowRecoverPassword(false))}
            className="recoverPassword-success-button blue-style"
          >
            {this.context.t('Close')}
          </button>
        </div>
      </div>
    );
  }

  render() {
    const { showRecoverSuccess } = this.state;
    const { isMobile } = this.props;

    return (
      <React.Fragment>
        <div className="recoverPassword" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          padding: isMobile ? '12px' : '20px',
        }}>
          <div
            className="recoverPassword-backdrop"
            onClick={() => this.props.dispatch(setShowRecoverPassword(false))}
          />

          <div className="recoverPassword-content">
            {showRecoverSuccess ? this.renderSuccessModal() : this.renderRecoverPasswordForm()}
          </div>
        </div>
        {this.state.showLinkSentModal && (
          <LoyaltyActivationLinkSentModal t={this.context.t} onClose={this.onCloseLinkSentModal} />
        )}
      </React.Fragment>
    );
  }
}

RecoverPasswordModal.contextTypes = {
  t: PropTypes.func.isRequired,
};

// Conectar con Redux para obtener isMobile
const mapStateToProps = (state) => ({
  isMobile: state.booking.device === 'MOBILE',
});

export default connect(mapStateToProps)(RecoverPasswordModal);
